// src/pages/settings/ClientsTab.tsx — onglet Clients (liste, recherche, création/édition/suppression)

import { useState } from "react";
import type { ConfirmModalProps } from "../../components/ConfirmModal";
import { BtnEdit, BtnDelete } from "../../components/IconButtons";
import type { ClientRecord, SiteRecord } from "../../api/settingsApi";
import Modal from "./Modal";
import { ClientForm } from "./ClientForm";

export function ClientsTab({ clients, sites, onSave, onDelete, onRefresh, setModal, showToast }:{
  clients: ClientRecord[]; sites: SiteRecord[];
  onSave: (c: ClientRecord) => Promise<void>;
  onDelete: (c: ClientRecord) => Promise<void>;
  onRefresh: () => Promise<void>;
  setModal: (m: ConfirmModalProps | null) => void;
  showToast: (msg: string) => void;
}) {
  const [showForm,   setShowForm]   = useState(false);
  const [editClient, setEditClient] = useState<ClientRecord|undefined>();
  const [query,      setQuery]      = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await onRefresh(); } finally { setRefreshing(false); }
  };

  const q = query.trim().toLowerCase();
  const filtered = q ? clients.filter(c => c.name.toLowerCase().includes(q)) : clients;

  const handleSave = async (c: ClientRecord) => {
    await onSave(c);
    showToast("Client enregistré ✓");
  };

  const handleDelete = (c: ClientRecord) => {
    setModal({ title:"Supprimer ce client ?",
      message:`Le client "${c.name}" et tous ses sites seront supprimés.`,
      confirmLabel:"Supprimer", danger:true, onCancel:()=>setModal(null),
      onConfirm: async () => { await onDelete(c); setModal(null); showToast("Client supprimé"); },
    });
  };

  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column"}}>
      <div style={{flexShrink:0,paddingBottom:12}}>
        <div className="flex-between mb-12">
          <p style={{fontSize:13,color:"var(--text-muted)"}}>
            {clients.length} client{clients.length!==1?"s":""} configuré{clients.length!==1?"s":""}.
          </p>
          <div className="flex gap-8">
            <button onClick={handleRefresh} disabled={refreshing} className="btn btn-ghost btn-sm">↻ Rafraîchir</button>
            <button onClick={()=>{setEditClient(undefined);setShowForm(true);}} className="btn btn-black btn-sm" style={{border:"1.5px solid transparent"}}>＋ Ajouter un client</button>
          </div>
        </div>
        <input
          type="text" placeholder="Rechercher un client…"
          value={query} onChange={e=>setQuery(e.target.value)}
          className="field" style={{width:"100%",fontSize:13}}
        />
      </div>
      <div style={{flex:1,minHeight:0,overflowY:"auto",paddingBottom:24}}>
        {clients.length===0
          ? <div className="card p-32" style={{textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:10}}>🏢</div>
              <p style={{fontSize:13,color:"var(--text-muted)",marginBottom:16}}>Aucun client configuré.</p>
              <button onClick={()=>{setEditClient(undefined);setShowForm(true);}} className="btn btn-black btn-sm">＋ Ajouter un client</button>
            </div>
          : filtered.length===0
            ? <div className="card p-32" style={{textAlign:"center",color:"var(--text-faint)"}}>
                <div style={{fontSize:28,marginBottom:10}}>🔎</div>
                <p style={{fontSize:13}}>Aucun résultat pour « {query} ».</p>
              </div>
            : <div className="flex-col gap-10">
                {filtered.map(c=>(
                  <div key={c.id} className="card flex gap-16" style={{alignItems:"center",padding:"14px 18px"}}>
                    {c.logoBase64
                      ? <img src={c.logoBase64} alt="" style={{width:40,height:40,objectFit:"contain",borderRadius:6,border:"1px solid #eee",flexShrink:0}}/>
                      : <div style={{width:40,height:40,borderRadius:6,background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🏢</div>}
                    <div className="flex-1">
                      <div className="t-title" style={{fontSize:14,color:"var(--text-primary)"}}>{c.name}</div>
                      <div style={{fontSize:11,color:"var(--text-muted)"}}>{(n=>n===0?"Aucun site":`${n} site${n>1?"s":""}`)(sites.filter(s=>s.clientId===c.id).length)}</div>
                    </div>
                    <div className="flex gap-8">
                      <BtnEdit onClick={()=>{setEditClient(c);setShowForm(true);}} title="Modifier le client"/>
                      <BtnDelete onClick={()=>handleDelete(c)} title="Supprimer le client"/>
                    </div>
                  </div>
                ))}
              </div>
        }
      </div>

      {showForm && (
        <Modal title={editClient?"Modifier le client":"Nouveau client"} onClose={()=>setShowForm(false)}>
          <ClientForm initial={editClient} onSave={handleSave} onClose={()=>setShowForm(false)}/>
        </Modal>
      )}
    </div>
  );
}
